import { Icon, IconButton, Input, InputGroup } from '@chakra-ui/react'
import { X } from 'lucide-react'
import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'

interface FilterMenuInputProps {
  /**
   * Current value of the input
   */
  value: string

  /**
   * Function called when input value changes
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void

  /**
   * Placeholder text for the input
   */
  placeholder?: string

  /**
   * Function to clear the input value
   */
  onClear?: () => void

  /**
   * Function called when key is pressed
   */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void

  /**
   * Function called on blur
   */
  onBlur?: () => void

  /**
   * Reference to the input element
   */
  inputRef?: RefObject<HTMLInputElement | null>

  /**
   * Additional props to be spread on the input
   */
  inputProps?: Record<string, unknown>
}

/**
 * A reusable input component for filter menus that includes
 * styling, placeholder, and a clear button.
 */
export function FilterMenuInput({
  value,
  onChange,
  placeholder,
  onClear,
  onKeyDown,
  onBlur,
  inputRef,
  inputProps = {},
}: FilterMenuInputProps) {
  const { t } = useTranslation()
  const defaultInputRef = useRef<HTMLInputElement>(null)
  const actualInputRef = inputRef || defaultInputRef

  // Handle clear button click
  const handleClearClick = (e: MouseEvent) => {
    e.stopPropagation()
    if (onClear) {
      onClear()
    }
    // Focus the input after clearing
    if (actualInputRef.current) {
      actualInputRef.current.focus()
    }
  }

  return (
    <InputGroup
      endElement={
        value ? (
          <IconButton
            variant="ghost"
            aria-label={t('components.filterMenu.clearFilter') as string}
            size="xs"
            onClick={handleClearClick}
            _hover={{ bg: 'transparent', opacity: 0.8 }}
          >
            <Icon as={X} />
          </IconButton>
        ) : undefined
      }
    >
      <Input
        ref={actualInputRef}
        placeholder={
          placeholder ||
          (t('components.filterMenu.searchPlaceholder') as string)
        }
        value={value}
        onChange={onChange}
        backgroundColor="background.bg-base"
        borderRadius="md"
        width="100%"
        size="sm"
        onBlur={onBlur}
        onClick={(e) => e.stopPropagation()} // Prevent menu close
        onKeyDown={onKeyDown}
        {...inputProps}
      />
    </InputGroup>
  )
}
